import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap, filter, take } from 'rxjs/operators';
import { LanguageActions } from './language.actions';
import { AuthActions } from '@app/store/auth/auth.actions';
import { Language } from '@app/shared/models';
import { LoggerService } from '@app/core/infrastructure';
import { UserPreferencesFacade } from '@app/store/user-preferences/user-preferences.facade';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class LanguageEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly preferencesFacade = inject(UserPreferencesFacade);
  private readonly logger = inject(LoggerService).scope('LanguageEffects');
  private readonly translateService = inject(TranslateService);
  
  private readonly STORAGE_KEY = 'active_language';

  /**
   * Initialize language on app startup
   */
  initialize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguageActions.initialize),
      map(() => {
        const storedLanguage = localStorage.getItem(this.STORAGE_KEY);
        
        // Use stored language if valid, otherwise detect browser language
        const language = (storedLanguage && Object.values(Language).includes(storedLanguage as Language))
          ? storedLanguage as Language
          : this.getBrowserLanguage();
        
        // Set the translation service language
        this.translateService.setDefaultLang('en');
        this.translateService.use(language.toLowerCase());
        
        this.logger.debug('Initialize language:', language);
        return LanguageActions.initializeSuccess({ language });
      }),
      catchError(error => {
        this.logger.error('Initialize failed:', error);
        return of(LanguageActions.initializeFailure({ error: error.message }));
      })
    )
  );

  /**
   * Load user preferred language when authentication completes
   * Listens to the userBackendSyncCompleted action to trigger on every login,
   * not just the first one in a session.
   */
  loadUserPreferredLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.userBackendSyncCompleted),
      switchMap(() =>
        this.preferencesFacade.waitForPreferences().pipe(
          map(preferences => {
            if (preferences.preferred_language && Object.values(Language).includes(preferences.preferred_language as Language)) {
              this.logger.debug('User preferred language loaded:', preferences.preferred_language);
              return LanguageActions.loadUserPreferredLanguageSuccess({ language: preferences.preferred_language as Language });
            }
            return LanguageActions.loadUserPreferredLanguageSuccess({ language: null });
          }),
          catchError(error => {
            this.logger.warn('Load user preferred language failed:', error);
            return of(LanguageActions.loadUserPreferredLanguageFailure({ error: error.message }));
          })
        )
      )
    )
  );

  /**
   * Apply translation when language changes in state
   * This handles both user preferred language loading and backend updates
   */
  applyLanguageChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LanguageActions.loadUserPreferredLanguageSuccess,
        LanguageActions.updateUserPreferredLanguageSuccess,
        LanguageActions.languageChangedFromBackend
      ),
      filter(({ language }) => language !== null),
      tap(({ language }) => {
        // Apply the translation and persist to localStorage
        // Safe to use non-null assertion since we filtered above
        localStorage.setItem(this.STORAGE_KEY, language!);
        this.translateService.use(language!.toLowerCase());
        this.logger.debug('Language applied from state change:', language);
      })
    ),
    { dispatch: false }
  );

  /**
   * Set active language and persist to localStorage
   */
  setActiveLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguageActions.setActiveLanguage),
      tap(({ language }) => {
        localStorage.setItem(this.STORAGE_KEY, language);
        this.translateService.use(language.toLowerCase());
        this.logger.debug('Active language set:', language);
      }),
      map(({ language }) => LanguageActions.setActiveLanguageSuccess({ language })),
      catchError(error => {
        this.logger.error('Set active language failed:', error);
        return of(LanguageActions.setActiveLanguageFailure({ error: error.message }));
      })
    )
  );

  /**
   * Update user preferred language on server
   */
  updateUserPreferredLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguageActions.updateUserPreferredLanguage),
      tap(({ language }) => {
        // Update via UserPreferencesFacade
        this.preferencesFacade.updatePreferences({ preferred_language: language });
        this.logger.debug('Updating user preferred language:', language);
      }),
      switchMap(({ language }) =>
        this.preferencesFacade.loading$.pipe(
          filter(loading => !loading),
          take(1),
          map(() => {
            this.logger.debug('User preferred language updated successfully');
            return LanguageActions.updateUserPreferredLanguageSuccess({ language });
          }),
          catchError(error => {
            this.logger.warn('Update user preferred language failed:', error);
            return of(LanguageActions.updateUserPreferredLanguageFailure({ error: error.message }));
          })
        )
      )
    )
  );

  /**
   * Reset language on logout
   * Clears localStorage and resets TranslateService to default language
   */
  resetLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguageActions.resetLanguage),
      tap(() => {
        localStorage.removeItem(this.STORAGE_KEY);
        // Reset TranslateService to default English
        this.translateService.use(Language.EN.toLowerCase());
        this.logger.debug('Language reset - localStorage cleared and UI reset to EN');
      })
    ),
    { dispatch: false }
  );

  /**
   * Initialize language on app startup
   */
  ngrxOnInitEffects(): Action {
    return LanguageActions.initialize();
  }

  /**
   * Detect browser language
   */
  private getBrowserLanguage(): Language {
    const browserLang = navigator.language || (navigator as any).userLanguage;
    const primaryLang = browserLang.split('-')[0].toLowerCase();
    
    // Map browser language codes to our Language enum
    switch (primaryLang) {
      case 'en': return Language.EN;
      case 'de': return Language.DE;
      case 'fr': return Language.FR;
      case 'es': return Language.ES;
      case 'pt': return Language.PT;
      case 'it': return Language.IT;
      case 'uk': return Language.UK;
      default: return Language.EN;
    }
  }
}
